import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../Avatar';
import { PatientLink } from '../../billing/PatientLink';
import { PracticeDoctorLabel } from '../../shared/PracticeDoctorLabel';
import { VersionBadge } from '../../shared/VersionBadge';

const MONITORING_TAB_SEARCH = '?tab=monitoring';

export interface MonitoringPatientSummary {
    patientId: string;
    patientName: string;
    practiceId: string | null;
    practiceName: string | null;
    doctorName: string | null;
    profileThumbnailUrl: string | null;
    version?: string | null;
}

// Where the avatar and name lead instead of the patient file.
export interface PatientIdentityLink {
    to: string;
    state?: unknown;
}

type IdentitySize = 'row' | 'header';

const SIZE_CLASSES: Record<IdentitySize, { avatar: string; gap: string; name: string }> = {
    row: { avatar: 'h-[30px] w-[30px]', gap: 'gap-2.5', name: 'text-[14px] font-medium' },
    header: { avatar: 'h-10 w-10', gap: 'gap-3', name: 'text-[15px] font-semibold' }
};

interface MonitoringPatientIdentityProps {
    patient: MonitoringPatientSummary;
    size?: IdentitySize;
    link?: PatientIdentityLink;
}

interface NameLinkProps {
    patient: MonitoringPatientSummary;
    link?: PatientIdentityLink;
    className: string;
    children: ReactNode;
}

// Same arrow, size and colour as the Conversations panel header's name link.
function NameLink({ patient, link, className, children }: NameLinkProps) {
    if (link) {
        return (
            <Link
                to={link.to}
                state={link.state}
                className="group flex min-w-0 items-center gap-1.5"
            >
                <span className={`${className} group-hover:underline`}>{children}</span>
                <ChevronRightIcon
                    aria-hidden="true"
                    className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0"
                />
            </Link>
        );
    }
    return (
        <PatientLink
            patientId={patient.patientId}
            patientName={patient.patientName}
            search={MONITORING_TAB_SEARCH}
            className={className}
        >
            {children}
        </PatientLink>
    );
}

export function MonitoringPatientIdentity({
    patient,
    size = 'row',
    link
}: MonitoringPatientIdentityProps) {
    const classes = SIZE_CLASSES[size];
    const avatar = (
        <Avatar
            user={{ name: patient.patientName, email: '' }}
            thumbnailURL={patient.profileThumbnailUrl ?? undefined}
        />
    );
    return (
        <div className={`flex min-w-0 items-center ${classes.gap}`}>
            {link ? (
                // A second pointer target for the name's link, so it stays out of the tab order.
                <Link
                    to={link.to}
                    state={link.state}
                    tabIndex={-1}
                    aria-hidden="true"
                    className={`${classes.avatar} block flex-shrink-0`}
                >
                    {avatar}
                </Link>
            ) : (
                <div className={`${classes.avatar} flex-shrink-0`}>{avatar}</div>
            )}
            <div className="flex min-w-0 flex-col leading-tight">
                <div className="flex min-w-0 items-center gap-1.5">
                    <NameLink
                        patient={patient}
                        link={link}
                        className={`block truncate ${classes.name} text-gray-900 hover:underline`}
                    >
                        {patient.patientName || 'Unnamed patient'}
                    </NameLink>
                    <VersionBadge value={patient.version} className="flex-shrink-0" />
                </div>
                <PracticeDoctorLabel
                    practiceName={patient.practiceName}
                    practiceId={patient.practiceId}
                    doctorName={patient.doctorName}
                    format="practice-doctor"
                    className="mt-0.5 block max-w-full truncate text-xs"
                    toneClassName="text-gray-500"
                />
            </div>
        </div>
    );
}
